import { Input, InputAddon, InputGroup, InputWrapper } from '@eigtech/ui/components/input'
import { HTMLInputTypeAttribute } from 'react'
import { FieldValues, useController } from 'react-hook-form'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  inputProps?: React.ComponentProps<typeof Input>
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
} & BaseProps<TFieldValues>

export type InputFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameAndDefaultValueProps<Props<TFieldValues>, TFieldValues> & {
    type?: HTMLInputTypeAttribute
  }

export function InputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  inputProps,
  leftAddon,
  rightAddon,
  leftElement,
  rightElement,
  defaultValue,
  type,
  ...props
}: InputFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  const input = (
    <Input
      {...field}
      disabled={isSubmitting}
      type={type}
      {...inputProps}
      value={field.value ?? ''}
    />
  )

  return (
    <FieldControl control={control} label={label} name={name} {...props}>
      {leftAddon || leftElement || rightElement || rightAddon ? (
        <InputGroup>
          {typeof leftAddon === 'string' ? <InputAddon>{leftAddon}</InputAddon> : leftAddon}

          <InputWrapper>
            {leftElement}

            {input}

            {rightElement}
          </InputWrapper>

          {typeof rightAddon === 'string' ? <InputAddon>{rightAddon}</InputAddon> : rightAddon}
        </InputGroup>
      ) : (
        input
      )}
    </FieldControl>
  )
}

export type InputFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof InputField<TFieldValues>
